import React from "react";

export function Section({
    title,
    icon,
    children,
    onSave,
    saving,
    saved,
    hasChanges,
}: {
    title: string;
    icon: string;
    children: React.ReactNode;
    onSave?: () => void;
    saving?: boolean;
    saved?: boolean;
    hasChanges?: boolean;
}) {
    return (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <span className="text-2xl">{icon}</span>
                    <h2 className="text-xl font-bold text-white">{title}</h2>
                </div>
                <div className="flex items-center gap-2">
                    {saved && (
                        <span className="text-xs text-green-400">✓ Saved</span>
                    )}
                    {onSave && hasChanges && (
                        <button
                            onClick={onSave}
                            disabled={saving}
                            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white text-sm font-medium rounded-lg transition-all"
                        >
                            {saving ? "Saving..." : "Save"}
                        </button>
                    )}
                </div>
            </div>
            {children}
        </div>
    );
}

export function NumberInput({
    label,
    value,
    onChange,
    min = 0,
    max,
    step = 1,
    description,
    compact,
}: {
    label: string;
    value: number | "";
    onChange: (v: number) => void;
    min?: number;
    max?: number;
    step?: number;
    description?: string;
    compact?: boolean;
}) {
    return (
        <div>
            <label className={`block text-slate-400 ${compact ? "text-xs" : "text-sm"} mb-1.5`}>{label}</label>
            <input
                type="number"
                value={value}
                onChange={(e) => {
                    const val = e.target.value;
                    if (val === "") {
                        // Keep as empty string to allow typing
                        onChange("" as any);
                    } else {
                        onChange(parseFloat(val));
                    }
                }}
                min={min}
                max={max}
                step={step}
                className={`w-full bg-slate-800 border border-slate-700 rounded-lg px-3 ${compact ? "py-1.5 text-sm" : "py-2"} text-white focus:border-blue-500 focus:outline-none`}
            />
            {description && <p className="text-slate-500 text-xs mt-1">{description}</p>}
        </div>
    );
}

export function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
    return (
        <label className="flex items-center gap-3 cursor-pointer">
            <div
                className={`w-11 h-6 rounded-full p-1 transition-colors ${checked ? "bg-blue-600" : "bg-slate-700"
                    }`}
                onClick={() => onChange(!checked)}
            >
                <div
                    className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0"
                        }`}
                />
            </div>
            <span className="text-slate-300 text-sm">{label}</span>
        </label>
    );
}

export const MessageIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);

export const MicIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1v10" />
        <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
        <rect x="8" y="3" width="8" height="12" rx="4" />
    </svg>
);

export const TicketIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
        <path d="M13 5v2" />
        <path d="M13 17v2" />
        <path d="M13 11v2" />
    </svg>
);

export function IconSelector({
    value,
    onChange
}: {
    value: "mic" | "message" | "ticket";
    onChange: (v: "mic" | "message" | "ticket") => void;
}) {
    const options = [
        { id: "mic", icon: <MicIcon size={16} />, label: "Mic" },
        { id: "message", icon: <MessageIcon size={16} />, label: "Msg" },
        { id: "ticket", icon: <TicketIcon size={16} />, label: "Pass" },
    ] as const;

    return (
        <div className="flex gap-1.5 bg-slate-900 p-1 rounded-lg border border-slate-700">
            {options.map((opt) => (
                <button
                    key={opt.id}
                    onClick={() => onChange(opt.id)}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all ${value === opt.id
                        ? "bg-blue-600 text-white shadow-lg"
                        : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                        }`}
                    title={opt.label}
                >
                    {opt.icon}
                </button>
            ))}
        </div>
    );
}
